/**
 * A strobe display.
 *
 * A needle tells you how far off you are; a strobe tells you whether you are
 * still moving. The bands drift at a rate proportional to the error and stop
 * dead when it reaches zero, and the eye is far better at spotting "that has
 * stopped" than at reading a needle's last two cents.
 *
 * Three rows at increasing sensitivity: the top row stays readable across the
 * whole range, the bottom row is still visibly crawling at a fifth of a cent.
 * Any multiplier preserves the property that matters — motionless means in
 * tune — so the rates are chosen for legibility rather than physical fidelity.
 */

const ROWS: readonly { multiplier: number; label: string }[] = [
  { multiplier: 1, label: "coarse" },
  { multiplier: 3, label: "fine" },
  { multiplier: 10, label: "finest" },
];

/** Cents of error that make the coarse row advance one band per second. */
const CENTS_PER_REVOLUTION = 10;
const BANDS_PER_ROW = 16;

export class Strobe {
  private readonly context: CanvasRenderingContext2D;
  private phase = 0;
  private cents = 0;
  private live = false;
  private lastFrame = 0;
  private frame = 0;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is not available.");
    this.context = context;
  }

  /** Current deviation in cents, or null when nothing is being heard. */
  setDeviation(cents: number | null): void {
    if (cents === null) {
      this.live = false;
      return;
    }
    this.live = true;
    this.cents = cents;
  }

  start(): void {
    if (this.frame) return;
    this.lastFrame = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - this.lastFrame) / 1000);
      this.lastFrame = now;
      if (this.live) this.phase += (this.cents / CENTS_PER_REVOLUTION) * dt;
      this.draw();
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  /** Match the backing store to the element's CSS size and pixel density. */
  resize(): void {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(this.canvas.clientWidth * ratio));
    const height = Math.max(1, Math.round(this.canvas.clientHeight * ratio));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }

  private draw(): void {
    const ctx = this.context;
    const { width, height } = this.canvas;
    const rowHeight = height / ROWS.length;
    const bandWidth = width / BANDS_PER_ROW;

    ctx.clearRect(0, 0, width, height);

    ROWS.forEach((row, index) => {
      const top = index * rowHeight;
      const inset = rowHeight * 0.14;

      ctx.fillStyle = "rgba(255,255,255,0.05)";
      ctx.fillRect(0, top + inset, width, rowHeight - inset * 2);

      if (!this.live) return;

      // Offset within one band pair, wrapped so the pattern never runs away.
      const cycle = this.phase * row.multiplier;
      const offset = ((cycle % 1) + 1) % 1;
      const shift = offset * bandWidth * 2;

      const intensity = index === ROWS.length - 1 ? 0.82 : 0.62;
      ctx.fillStyle = `rgba(126, 224, 199, ${intensity})`;
      for (let band = -1; band < BANDS_PER_ROW + 1; band += 2) {
        ctx.fillRect(band * bandWidth + shift, top + inset, bandWidth, rowHeight - inset * 2);
      }
    });

    // Centre line, to read the drift against.
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.fillRect(width / 2 - 1, 0, 2, height);
  }
}
